import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
  TrendingUp,
  AlertTriangle,
  XCircle,
  BarChart3,
  ScrollText,
  Activity,
  Radio,
  Keyboard,
  Swords,
} from "lucide-react";

const TABS = [
  { to: "/app/terminal", label: "BEST EDGE", end: true, icon: TrendingUp, key: "1" },
  { to: "/app/terminal/traps", label: "TRAPS", icon: AlertTriangle, key: "2" },
  { to: "/app/terminal/no-bet", label: "NO-BET", icon: XCircle, key: "3" },
  { to: "/app/terminal/disagreement", label: "DISAGREEMENT", icon: BarChart3, key: "4" },
  { to: "/app/terminal/ledger", label: "LEDGER", icon: ScrollText, key: "5" },
  { to: "/app/terminal/ufc", label: "UFC", icon: Swords, key: "6" },
];

function TickerCell({ label, value, tone = "neutral", testid }) {
  const toneClass =
    tone === "good"
      ? "text-[#00D4FF]"
      : tone === "warn"
      ? "text-[#FFBF00]"
      : tone === "bad"
      ? "text-red-400"
      : "text-zinc-200";
  return (
    <div className="flex items-baseline gap-2 whitespace-nowrap" data-testid={testid}>
      <span className="overline text-zinc-500">{label}</span>
      <span className={`mono text-sm font-bold ${toneClass}`}>{value}</span>
    </div>
  );
}

export function TerminalTicker() {
  const [ticker, setTicker] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/terminal/ticker");
      setTicker(data);
    } catch (err) {
      console.warn("ticker load failed", err);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  if (!ticker) {
    return (
      <div className="border-b border-zinc-800 bg-zinc-950/80 px-6 py-2 mono text-xs text-zinc-500">
        <Radio size={10} className="inline animate-pulse mr-2" />
        Terminal booting…
      </div>
    );
  }

  const be = ticker.best_edge || {};
  const led = ticker.ledger || {};

  return (
    <div
      className="border-b border-zinc-800 bg-zinc-950/80 px-6 py-2 flex flex-wrap items-center gap-5 text-xs"
      data-testid="terminal-ticker"
    >
      <div className="flex items-center gap-1.5 text-[#00D4FF]">
        <Radio size={10} className="animate-pulse" />
        <span className="mono font-bold tracking-wider">LIVE</span>
      </div>
      <TickerCell label="SLATE" value={ticker.slate_count} testid="ticker-slate" />
      <TickerCell label="+EV" value={ticker.plus_ev_count} tone="good" testid="ticker-plus-ev" />
      <TickerCell label="TRAPS" value={ticker.traps_count} tone="warn" testid="ticker-traps" />
      <TickerCell label="NO-BET" value={ticker.no_bet_count} tone="bad" testid="ticker-no-bet" />
      <div className="hidden md:flex items-baseline gap-2 border-l border-zinc-800 pl-5">
        <span className="overline text-zinc-500">BEST</span>
        <span className="mono text-sm text-zinc-200 truncate max-w-[22rem]">
          {be.lean || "—"}
        </span>
        {Number.isFinite(be.edge_pct) && (
          <span className="mono text-sm font-bold text-[#00D4FF]">
            {be.edge_pct > 0 ? `+${be.edge_pct.toFixed(1)}%` : `${be.edge_pct.toFixed(1)}%`}
          </span>
        )}
      </div>
      <div className="hidden lg:flex items-baseline gap-2 border-l border-zinc-800 pl-5">
        <span className="overline text-zinc-500">LEDGER</span>
        <span className="mono text-sm">
          <span className="text-[#00D4FF]">{led.wins || 0}W</span>
          <span className="text-zinc-500"> / </span>
          <span className="text-red-400">{led.losses || 0}L</span>
          <span className="text-zinc-500"> · </span>
          <span className="text-zinc-200">{(led.hit_rate_pct ?? 0).toFixed(1)}%</span>
          {led.avg_clv_pct !== null && led.avg_clv_pct !== undefined && (
            <>
              <span className="text-zinc-500"> · CLV </span>
              <span className={led.avg_clv_pct >= 0 ? "text-[#00D4FF]" : "text-red-400"}>
                {led.avg_clv_pct >= 0 ? `+${led.avg_clv_pct}` : led.avg_clv_pct}%
              </span>
            </>
          )}
        </span>
      </div>
      <button
        data-testid="ticker-refresh"
        onClick={load}
        className="ml-auto mono text-[10px] text-zinc-500 hover:text-[#00D4FF] tracking-wider"
      >
        SYNC
      </button>
    </div>
  );
}

export default function TerminalShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    function onKey(e) {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const hit = TABS.find((tb) => tb.key === e.key);
      if (hit) {
        e.preventDefault();
        navigate(hit.to);
        return;
      }
      if (e.key === "?") {
        e.preventDefault();
        setShowHelp((v) => !v);
      }
      if (e.key === "Escape") setShowHelp(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div data-testid="terminal-shell">
      {/* Title bar */}
      <div className="flex items-baseline justify-between border-b border-zinc-800 pb-4 mb-0">
        <div>
          <div className="overline neon-text mb-1 flex items-center gap-2">
            <Activity size={10} strokeWidth={1.75} />
            MARTINGALE·TERMINAL
          </div>
          <h1 className="font-display text-4xl lg:text-5xl leading-none">
            Today's tape.
          </h1>
          <p className="mono text-[11px] text-zinc-500 mt-2">
            Pre-evaluated every 3 hours · DK prices snapped 15 min before kickoff · CLV graded after final
          </p>
        </div>
        <div className="hidden md:flex flex-col items-end gap-2">
          <Link
            to="/app"
            className="mono text-[11px] text-zinc-500 hover:text-[#00D4FF] tracking-wider"
            data-testid="terminal-back-dashboard"
          >
            ← DASHBOARD
          </Link>
          <button
            onClick={() => setShowHelp((v) => !v)}
            data-testid="terminal-help-toggle"
            className="mono text-[10px] text-zinc-500 hover:text-[#00D4FF] tracking-wider flex items-center gap-1.5"
          >
            <Keyboard size={11} strokeWidth={1.5} />
            SHORTCUTS · ?
          </button>
        </div>
      </div>

      {showHelp && (
        <div
          className="border border-[#00D4FF]/40 bg-zinc-950/90 backdrop-blur p-5 mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 mono text-[11px]"
          data-testid="terminal-shortcuts-panel"
        >
          <div className="overline col-span-full text-[#00D4FF] mb-1">KEYBOARD SHORTCUTS</div>
          {TABS.map((t) => (
            <div key={t.to} className="flex justify-between">
              <span className="text-zinc-400">Jump to {t.label}</span>
              <kbd className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-700 text-[#00D4FF]">{t.key}</kbd>
            </div>
          ))}
          <div className="flex justify-between">
            <span className="text-zinc-400">Toggle this panel</span>
            <kbd className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-700 text-[#00D4FF]">?</kbd>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">Close</span>
            <kbd className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-700 text-[#00D4FF]">esc</kbd>
          </div>
        </div>
      )}

      {/* Ticker */}
      <TerminalTicker />

      {/* Tab nav */}
      <nav className="flex flex-wrap border-b border-zinc-800" data-testid="terminal-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`terminal-tab-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 mono text-[11px] tracking-wider border-r border-zinc-800 ${
                  isActive
                    ? "bg-zinc-900/60 text-[#00D4FF] border-b-2 border-b-[#00D4FF]"
                    : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/30"
                }`
              }
            >
              <Icon size={12} strokeWidth={1.5} />
              {t.label}
              <span className="hidden lg:inline text-[9px] text-zinc-600 border border-zinc-800 px-1 ml-1">
                {t.key}
              </span>
            </NavLink>
          );
        })}
      </nav>

      {/* Active panel */}
      <div className="py-6" key={location.pathname}>
        <Outlet />
      </div>
    </div>
  );
}
